/**
 * 智能体节点执行器：两段式「规划 → 审阅 → 批量生成」
 *
 * 第一段 runAgentPlanning：合并上游文本与节点指令，调 /canvas/llm/plan 让 LLM
 * 产出「统一风格前缀 + 每张差异提示词」，回填节点供用户审阅编辑。
 *
 * 第二段 runAgentGeneration：按计划建 1 个结果图片组 + 冻结外框 + 一条
 * agent → 组血缘边（boardActions.addNodes 单次调用 = 单条撤销历史），不再
 * 派生生图工作卡——提示词编辑与单项重生成都收敛在智能体面板（计划项
 * 生成完成后仍可编辑），避免卡片/组/agent 三方状态错位，也避免工作卡被
 * 「执行全部」误跑。逐项并发提交 api.generateCanvasImage 并轮询，结果
 * 聚合进图片组；失败项可 retryAgentItems 单张重提（结果组失败槽位 /
 * 智能体面板单项重生 / 整批失败项重试三个入口共用）。
 *
 * 一致性策略（A 档）：风格圣经——stylePrefix 注入每张最终提示词；
 * 图片链路无 seed 字段，不做种子锁定。
 *
 * 失败语义：单项失败不影响其他项（尽力而为），失败项可 retryAgentItems
 * 单独重提；整批状态 done / partial_failed / failed 由 finalizeAgentStatus 收敛。
 */
import { api } from '../../../services/api';
import type { JobStatus, GenerationMode } from '../../../services/api';
import { boardActions, getBoardState } from '../state/boardStore';
import { aggregateUpstreamText, aggregateUpstreamReferenceImages, waitForUpstreamIdle } from '../state/upstream';
import { CANVAS_NODE_REMOVED, makeNode, nid } from '../utils';
import { DEFAULT_NODE_SIZES } from '../constants';
import { platformFetch } from '../../../platform/http';
import { patchImageGroupIndex, writeGroupIndexFromJob, finalizeImageGroup } from './imageGenerator';
import { fitAgentContainer } from './agentUnit';
import { getCachedImageModels } from '../modelCache';
import { validateImageReferenceQuota } from '../utils/referenceImageQuota';
import { resolveBlobUrlsForApi } from '../imageStorage';
import type {
  AgentNodeData,
  AgentPlanItem,
  CanvasEdge,
  CanvasNode,
  GroupNodeData,
  ImageGroupNodeData,
} from '../types';

/** 计划项数量上限（与后端 canvasLlmPlan 的 MAX_IMAGES 一致） */
export const MAX_AGENT_ITEMS = 8;

const DEFAULTS = {
  aspectRatio: 'auto',
  quality: 'low',
  mode: 'text2img' as GenerationMode,
};

interface PlanResponse {
  stylePrefix: string;
  images: AgentPlanItem[];
}

/** 最终提示词 = 风格圣经 + 单张差异（中文逗号连接） */
export function composeAgentPrompt(stylePrefix: string, prompt: string): string {
  return [stylePrefix.trim(), prompt.trim()].filter(Boolean).join('，');
}

function readAgentNode(nodeId: string): CanvasNode<AgentNodeData> | undefined {
  return getBoardState().nodes.find((n) => n.id === nodeId) as
    | CanvasNode<AgentNodeData>
    | undefined;
}

/** 读-改-写更新第 index 项的执行状态（同步执行无竞态） */
function updateAgentItem(nodeId: string, index: number, status: string): void {
  const node = readAgentNode(nodeId);
  if (!node) return;
  const itemStatuses = [...(node.data.itemStatuses ?? [])];
  if (itemStatuses[index] === status) return;
  itemStatuses[index] = status;
  boardActions.updateNodeData(nodeId, { itemStatuses });
}

/** 读取图片组某格的结果 URL（持久网关地址，可直接作为参考图输入） */
function readGroupImageUrl(groupNodeId: string, index: number): string | null {
  const node = getBoardState().nodes.find((n) => n.id === groupNodeId);
  const data = node ? (node.data as ImageGroupNodeData) : null;
  return data?.imageUrls?.[index] ?? null;
}

/** 所选模型是否支持"带 1 张参考图的图生图"（参考图链的能力前提）；模型信息缺失时乐观放行 */
async function modelSupportsChainReference(modelId: string): Promise<boolean> {
  try {
    const list = await getCachedImageModels();
    const model = list.find((m) => m.id === modelId);
    if (!model) return true;
    if (model.enableImg2img === false) return false;
    return validateImageReferenceQuota('img2img', 1, model.modeCapabilities) === null;
  } catch {
    return true;
  }
}

/** 取模型 img2img 模式能力（配额校验用）；模型信息缺失时返回 undefined（乐观放行） */
async function getModelImg2imgCaps(modelId: string) {
  try {
    const list = await getCachedImageModels();
    return list.find((m) => m.id === modelId)?.modeCapabilities;
  } catch {
    return undefined;
  }
}

/**
 * 解析上游参考图（image / image_group 逐张 / video 封面），blob URL 转 data URL。
 * 返回 null 表示连线了参考图但模型不支持对应数量的图生图（错误信息由调用方写入节点）。
 */
async function resolveUpstreamReferences(
  nodeId: string,
  modelId: string
): Promise<{ refs: string[]; error?: string }> {
  const { nodes, edges } = getBoardState();
  const raw = aggregateUpstreamReferenceImages(nodeId, nodes, edges);
  if (raw.length === 0) return { refs: [] };
  const caps = await getModelImg2imgCaps(modelId);
  const quotaError = validateImageReferenceQuota('img2img', raw.length, caps);
  if (quotaError) return { refs: [], error: `${quotaError}（智能体需要把上游图片作为参考注入每张生成）` };
  const resolved = await resolveBlobUrlsForApi(raw);
  if (resolved.length === 0) return { refs: [], error: '上游参考图解析失败，请重新连接或更换图片' };
  return { refs: resolved };
}

/**
 * 解析重试批次的参考锚点：优先图片组"主图"（用户可设），
 * 回退第一个已完成的格子；无任何完成格时返回 null（无锚可用）。
 */
function resolveRetryAnchor(groupNodeId: string): { index: number; url: string } | null {
  const node = getBoardState().nodes.find((n) => n.id === groupNodeId);
  const data = node ? (node.data as ImageGroupNodeData) : null;
  if (!data) return null;
  const jobIds = data.jobIds ?? [];
  const statuses = data.statuses ?? [];
  const imageUrls = data.imageUrls ?? [];
  const pick = (index: number): { index: number; url: string } | null =>
    statuses[index] === 'completed' && imageUrls[index] ? { index, url: imageUrls[index]! } : null;
  if (data.primaryJobId && jobIds.includes(data.primaryJobId)) {
    return pick(jobIds.indexOf(data.primaryJobId));
  }
  for (let i = 0; i < statuses.length; i++) {
    const found = pick(i);
    if (found) return found;
  }
  return null;
}

/** 整批终态收敛：全成 done / 全败 failed / 部分 partial_failed */
function finalizeAgentStatus(nodeId: string): void {
  const node = readAgentNode(nodeId);
  if (!node) return;
  const statuses = node.data.itemStatuses ?? [];
  const completed = statuses.filter((s) => s === 'completed').length;
  boardActions.updateNodeData(nodeId, {
    generating: false,
    status: completed === 0 ? 'failed' : completed === statuses.length ? 'done' : 'partial_failed',
  });
}

function makeLineageEdge(sourceNodeId: string, targetNodeId: string): CanvasEdge {
  return {
    id: nid(),
    sourceNodeId,
    targetNodeId,
    sourceHandle: 'right',
    targetHandle: 'left',
  };
}

/** 单项提交 + 轮询 + 三处回写（组格子 / agent 项状态 / 工作卡）；返回单项终态，失败不上抛 */
async function submitAgentItem(params: {
  agentNodeId: string;
  groupNodeId: string;
  childNodeId?: string;
  index: number;
  modelId: string;
  prompt: string;
  aspectRatio: string;
  quality: string;
  /** 参考图链：携带基准图时走 img2img，否则 text2img */
  referenceImages?: string[];
}): Promise<'completed' | 'failed'> {
  const { agentNodeId, groupNodeId, childNodeId, index, modelId, prompt, aspectRatio, quality } = params;
  const referenceImages = params.referenceImages?.length ? params.referenceImages : undefined;
  const mode: GenerationMode = referenceImages ? 'img2img' : DEFAULTS.mode;
  try {
    const resp = await api.generateCanvasImage({
      model: modelId,
      prompt,
      aspectRatio,
      quality,
      n: 1,
      mode,
      referenceImages,
      clientRequestId: nid(),
    });
    patchImageGroupIndex(groupNodeId, index, { jobId: resp.jobId });
    updateAgentItem(agentNodeId, index, 'processing');
    await api.pollJobStatus(resp.jobId, (job: JobStatus) => {
      // 组节点被撤销/删除时中断轮询，避免幽灵写入
      if (!getBoardState().nodes.some((n) => n.id === groupNodeId)) {
        throw new Error(CANVAS_NODE_REMOVED);
      }
      writeGroupIndexFromJob(groupNodeId, index, job);
    });
    updateAgentItem(agentNodeId, index, 'completed');
    if (childNodeId) {
      boardActions.updateNodeData(childNodeId, { generating: false, lastJobIds: [resp.jobId] });
    }
    return 'completed';
  } catch (err) {
    patchImageGroupIndex(groupNodeId, index, { status: 'failed' });
    updateAgentItem(agentNodeId, index, 'failed');
    if (childNodeId) {
      boardActions.updateNodeData(childNodeId, { generating: false });
    }
    // 组被删除属于主动取消；agent 语义为尽力而为，单项失败不上抛、由失败项重试兜底
    if (err instanceof Error && err.message === CANVAS_NODE_REMOVED) return 'failed';
    console.error('[agent-generator] 单项生成失败:', err);
    return 'failed';
  }
}

// ---------------- 第一段：规划 ----------------

export async function runAgentPlanning(nodeId: string): Promise<void> {
  const node = readAgentNode(nodeId);
  if (!node) return;
  if (node.data.generating) return;

  // 上游链路执行中：置生成中并等待完成（防等待期间重复触发；失败复位）
  boardActions.updateNodeData(nodeId, { generating: true, status: 'planning', errorMessage: null });
  try {
    await waitForUpstreamIdle(nodeId);
  } catch (err) {
    boardActions.updateNodeData(nodeId, {
      generating: false,
      status: 'failed',
      errorMessage: (err as Error).message || '规划失败',
    });
    return;
  }

  // 等待后重读最新节点与图状态（上游输出已落图）
  const freshNode = readAgentNode(nodeId);
  if (!freshNode) return;
  const instruction = (freshNode.data.instruction || '').trim();
  const { nodes, edges } = getBoardState();
  const upstreamText = aggregateUpstreamText(nodeId, nodes, edges);
  const upstreamRefCount = aggregateUpstreamReferenceImages(nodeId, nodes, edges).length;
  let merged = [upstreamText?.trim(), instruction].filter(Boolean).join('\n');
  if (!merged) {
    boardActions.updateNodeData(nodeId, {
      generating: false,
      status: 'failed',
      errorMessage: '请先填写指令或连接上游文本节点',
    });
    return;
  }
  // 有参考图时提示规划器围绕图中主体规划差异，避免计划与图片内容脱节
  if (upstreamRefCount > 0) {
    merged += `\n（已提供 ${upstreamRefCount} 张参考图作为主体基准，请围绕图中主体规划各张的差异视角/构图，stylePrefix 描述图中主体的关键特征）`;
  }

  try {
    const resp = await platformFetch('/canvas/llm/plan', {
      method: 'POST',
      body: JSON.stringify({ instruction: merged }),
    });
    if (!resp.ok) {
      const data = (await resp.json().catch(() => ({}))) as { error?: string };
      throw new Error(data.error || '规划失败');
    }
    const plan = (await resp.json()) as PlanResponse;
    const images = (plan.images || []).slice(0, MAX_AGENT_ITEMS);
    if (images.length === 0) throw new Error('规划结果为空，请调整指令后重试');

    boardActions.updateNodeData(nodeId, {
      generating: false,
      status: 'planned',
      stylePrefix: (plan.stylePrefix || '').trim(),
      items: images,
      itemStatuses: images.map(() => 'pending'),
      errorMessage: null,
    });
    boardActions.commitNodeMutation();
  } catch (err) {
    boardActions.updateNodeData(nodeId, {
      generating: false,
      status: 'failed',
      errorMessage: (err as Error).message || '规划失败',
    });
  }
}

// ---------------- 第二段：批量生成 ----------------

export async function runAgentGeneration(nodeId: string): Promise<void> {
  const node = readAgentNode(nodeId);
  if (!node) return;
  if (node.data.generating) return;

  // 上游链路执行中：置生成中并等待完成（防等待期间重复触发；失败复位），
  // 等待后重读节点与图状态（上游输出、参考图已就绪）
  boardActions.updateNodeData(nodeId, { generating: true, errorMessage: null });
  try {
    await waitForUpstreamIdle(nodeId);
  } catch (err) {
    boardActions.updateNodeData(nodeId, {
      generating: false,
      status: 'failed',
      errorMessage: (err as Error).message || '生成失败',
    });
    return;
  }
  const freshNode = readAgentNode(nodeId);
  if (!freshNode) return;

  const data = freshNode.data;
  const items = (data.items || [])
    .map((item) => ({ label: item.label, prompt: (item.prompt || '').trim() }))
    .filter((item) => item.prompt)
    .slice(0, MAX_AGENT_ITEMS);

  // 前置校验失败的 return 必须复位 generating，否则节点永久卡在"生成中"（刷新前无法再次运行）
  if (items.length === 0) {
    boardActions.updateNodeData(nodeId, { generating: false, errorMessage: '计划为空，请先规划或填写各图提示词' });
    return;
  }
  if (!data.modelId) {
    boardActions.updateNodeData(nodeId, { generating: false, errorMessage: '请先选择图片模型' });
    return;
  }

  const stylePrefix = (data.stylePrefix || '').trim();
  const aspectRatio = data.aspectRatio || DEFAULTS.aspectRatio;
  const quality = data.quality || DEFAULTS.quality;
  const itemStatuses = items.map(() => 'pending');

  // 上游参考图（image / image_group / video 封面）：注入每一张生成（含基准图），
  // 让"外接角色图 → 批量同主体不同角度"成立；配额/解析失败在此拦截
  const { refs: upstreamRefs, error: refsError } = await resolveUpstreamReferences(nodeId, data.modelId);
  if (refsError) {
    boardActions.updateNodeData(nodeId, { generating: false, errorMessage: refsError });
    return;
  }

  // 一次性建图：1 个结果图片组 + 冻结外框 + 一条 agent → 组血缘边 = 单条撤销历史。
  // 不再派生生图工作卡：提示词编辑/单项重生成都收敛在智能体面板（计划项
  // 生成完成后仍可编辑），结果组是批次唯一产物。
  // 外框为智能体批次专用分组容器（_autoGenerated）：成员按 agent 数据判定、
  // 尺寸由系统管理，与批次同建同删（冻结语义见 agentUnit）
  const groupPos = { x: node.x + node.width + 60, y: node.y };
  const groupSize = DEFAULT_NODE_SIZES['image_group'];
  const groupNode = makeNode<ImageGroupNodeData>(
    'image_group',
    groupPos,
    {
      jobIds: items.map(() => null),
      imageUrls: items.map(() => null),
      statuses: items.map(() => 'processing'),
      status: 'processing',
      _autoGenerated: true,
    } as ImageGroupNodeData & { _autoGenerated: boolean },
    undefined,
    node.zIndex
  );

  // 冻结外框 = agent + 结果组的联合包围盒 + 40 padding（终态后由
  // fitAgentContainer 按实际尺寸收缩）；zIndex -1 保证位于成员之下
  const PAD = 40;
  const containerRect = {
    x: Math.min(node.x, groupPos.x) - PAD,
    y: node.y - PAD,
    width: Math.max(node.x + node.width, groupPos.x + groupSize.width) - Math.min(node.x, groupPos.x) + PAD * 2,
    height: Math.max(node.y + node.height, groupPos.y + groupSize.height) - node.y + PAD * 2,
  };
  const containerNode = makeNode<GroupNodeData>(
    'group',
    { x: containerRect.x, y: containerRect.y },
    { label: '智能体批次', _autoGenerated: true },
    { width: containerRect.width, height: containerRect.height },
    -1
  );

  boardActions.addNodes([groupNode, containerNode], [makeLineageEdge(nodeId, groupNode.id)]);
  // addNodes 默认选中全部新建节点（选中即展开下方面板），批量召唤时面板会展开；
  // 收回选区到智能体自身，保持用户焦点
  boardActions.setSelection({ nodeIds: [nodeId], edgeIds: [] });

  // 参考图链：开关开启且模型支持 img2img 时，第 1 项作为基准图先行，
  // 其余各项携带基准图结果作为参考并发生成；基准失败或能力不足则整批退回纯文生图
  const chainEnabled =
    (data.useChainReference ?? true) &&
    items.length > 1 &&
    (await modelSupportsChainReference(data.modelId));

  boardActions.updateNodeData(nodeId, {
    generating: true,
    status: 'executing',
    items,
    itemStatuses,
    // 新批次无工作卡；显式置空以清掉存量批次的旧卡引用
    childNodeIds: [],
    groupNodeId: groupNode.id,
    containerNodeId: containerNode.id,
    chainApplied: chainEnabled,
    errorMessage: null,
  });

  const submitItem = (i: number, referenceImages?: string[]) =>
    submitAgentItem({
      agentNodeId: nodeId,
      groupNodeId: groupNode.id,
      index: i,
      modelId: data.modelId!,
      prompt: composeAgentPrompt(stylePrefix, items[i].prompt),
      aspectRatio,
      quality,
      referenceImages,
    });

  // 每张的参考组合：上游参考图（用户显式连接，优先保留）+ 参考链基准图（配额允许时叠加）
  const caps = await getModelImg2imgCaps(data.modelId);
  const refsForItem = (anchorUrl?: string | null): string[] | undefined => {
    const refs = [...upstreamRefs];
    if (anchorUrl && validateImageReferenceQuota('img2img', refs.length + 1, caps) === null) {
      refs.push(anchorUrl);
    }
    return refs.length > 0 ? refs : undefined;
  };

  if (chainEnabled) {
    // 基准图先行（带上游参考，不带链内基准）
    await submitItem(0, upstreamRefs.length > 0 ? [...upstreamRefs] : undefined);
    const anchorUrl = readGroupImageUrl(groupNode.id, 0);
    const restRefs = refsForItem(anchorUrl);
    const rest = items.slice(1).map((_, k) => submitItem(k + 1, restRefs));
    await Promise.allSettled(rest);
  } else {
    const refs = upstreamRefs.length > 0 ? [...upstreamRefs] : undefined;
    const all = items.map((_, i) => submitItem(i, refs));
    await Promise.allSettled(all);
  }

  finalizeImageGroup(groupNode.id);
  finalizeAgentStatus(nodeId);
  // 终态后按实际节点尺寸收缩冻结外框（建批时按默认尺寸估算，结果组随图片
  // 填充会自动增高，外框需要随之扩展到完全覆盖）
  fitAgentContainer(nodeId);
}

/**
 * 槽位重提：显式传 indexes 时无条件重提指定槽位（结果组失败槽位重试 /
 * 智能体面板单项重生成共用，编辑提示词后重跑即所见即所得）；缺省仅重提
 * itemStatuses 为 failed 的槽位（面板整批「重试失败项」）。不重建节点。
 */
export async function retryAgentItems(nodeId: string, indexes?: number[]): Promise<void> {
  const node = readAgentNode(nodeId);
  if (!node) return;
  if (node.data.generating) return;

  const data = node.data;
  if (!data.groupNodeId || !data.items?.length) {
    boardActions.updateNodeData(nodeId, { errorMessage: '没有可重试的批次' });
    return;
  }
  const targetIndexes =
    indexes ??
    (data.itemStatuses ?? [])
      .map((status, i) => ({ status, i }))
      .filter((x) => x.status === 'failed')
      .map((x) => x.i);
  if (targetIndexes.length === 0) return;
  // 显式指定的槽位必须落在计划范围内且有提示词
  const validIndexes = targetIndexes.filter(
    (i) => i >= 0 && i < data.items!.length && (data.items![i].prompt || '').trim()
  );
  if (validIndexes.length === 0) return;

  if (!data.modelId) {
    boardActions.updateNodeData(nodeId, { errorMessage: '请先选择图片模型' });
    return;
  }
  // 结果组节点已不在画布上（被撤销等）：无从回写，拦截
  if (!getBoardState().nodes.some((n) => n.id === data.groupNodeId)) {
    boardActions.updateNodeData(nodeId, { errorMessage: '结果图片组已不存在，请重新生成批次' });
    return;
  }

  const stylePrefix = (data.stylePrefix || '').trim();
  const aspectRatio = data.aspectRatio || DEFAULTS.aspectRatio;
  const quality = data.quality || DEFAULTS.quality;
  const childNodeIds = data.childNodeIds ?? [];

  boardActions.updateNodeData(nodeId, { generating: true, status: 'executing', errorMessage: null });
  for (const i of validIndexes) {
    updateAgentItem(nodeId, i, 'processing');
    patchImageGroupIndex(data.groupNodeId, i, { status: 'processing' });
  }

  // 参考锚点：开关开启且存在已完成锚点（优先图片组主图，未设回退第 1 格）时，
  // 重试项（锚点自身除外）携带锚点作为参考图重提；上游参考图同样注入
  const { refs: upstreamRefs, error: refsError } = await resolveUpstreamReferences(nodeId, data.modelId);
  if (refsError) {
    boardActions.updateNodeData(nodeId, { generating: false, errorMessage: refsError });
    return;
  }
  const anchor =
    (data.useChainReference ?? true) && (await modelSupportsChainReference(data.modelId))
      ? resolveRetryAnchor(data.groupNodeId)
      : null;
  const caps = await getModelImg2imgCaps(data.modelId);
  const refsForRetry = (index: number): string[] | undefined => {
    const refs = [...upstreamRefs];
    if (
      anchor &&
      anchor.index !== index &&
      validateImageReferenceQuota('img2img', refs.length + 1, caps) === null
    ) {
      refs.push(anchor.url);
    }
    return refs.length > 0 ? refs : undefined;
  };

  const tasks = validIndexes.map((i) =>
    submitAgentItem({
      agentNodeId: nodeId,
      groupNodeId: data.groupNodeId!,
      // 存量批次仍写回工作卡状态；新批次无卡（undefined 安全跳过）
      childNodeId: childNodeIds[i],
      index: i,
      modelId: data.modelId!,
      prompt: composeAgentPrompt(stylePrefix, data.items![i].prompt),
      aspectRatio,
      quality,
      referenceImages: refsForRetry(i),
    })
  );
  await Promise.allSettled(tasks);

  finalizeImageGroup(data.groupNodeId);
  finalizeAgentStatus(nodeId);
  fitAgentContainer(nodeId);
}
